import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart2,
  Landmark,
  Layers,
  Loader2,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Alert,
  EmptyState,
  Select,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '../ui';
import { portfolioService } from '../../services/portfolio.service';
import { customersService } from '../../services/customers.service';
import {
  PORTFOLIO_DISPLAY_CURRENCIES,
  amountOrZero,
  assetRow,
  cashRow,
  normalizeCustomerPortfolio,
} from './portfolioModel';

const ALLOC_COLORS = ['#4f46e5', '#0d9488', '#d97706', '#dc2626', '#2563eb', '#7c3aed', '#059669'];

const fmtNum = (n, d = 2) =>
  n != null && n !== ''
    ? Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })
    : '—';

const fmtQty = (n) => {
  if (n == null || n === '') return '—';
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString('en-US', { maximumFractionDigits: 8 });
};

const fmtPct = (n) => {
  if (n == null || n === '') return null;
  const num = Number(n);
  if (!Number.isFinite(num)) return null;
  const sign = num > 0 ? '+' : '';
  return `${sign}${num.toFixed(2)}%`;
};

const fmtMoney = (n, currency) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const formatted = fmtNum(n);
  return currency ? `${currency} ${formatted}` : formatted;
};

const fmtSignedMoney = (n, currency) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const sign = n > 0 ? '+' : n < 0 ? '-' : '';
  return `${sign}${fmtMoney(Math.abs(n), currency)}`;
};

const PnL = ({ value, pct, align = 'right', native, nativeCurrency }) => {
  if (value == null || value === '') {
    return <span className="font-mono text-sm tnum text-ink-400">—</span>;
  }
  const num = Number(value);
  const pos = num >= 0;
  const pctLabel = fmtPct(pct);
  return (
    <div className={`flex flex-col ${align === 'right' ? 'items-end' : 'items-start'} ${pos ? 'text-success-700' : 'text-danger-700'}`}>
      <div className="flex items-center gap-1">
        {num === 0 ? null : pos ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
        <span className="font-mono text-sm tnum">{fmtSignedMoney(num)}</span>
      </div>
      {pctLabel && <span className="text-xs font-mono tnum opacity-70">{pctLabel}</span>}
      {nativeCurrency && native != null && (
        <span className="text-2xs font-mono tnum text-ink-400">
          {nativeCurrency} {fmtSignedMoney(native)}
        </span>
      )}
    </div>
  );
};

const NativeLine = ({ show, currency, value }) => {
  if (!show || value == null) return null;
  return (
    <p className="text-2xs font-mono tnum text-ink-400">
      {currency} {fmtNum(value)}
    </p>
  );
};

const Mark = ({ logoUrl, label }) => {
  const [failed, setFailed] = useState(false);
  const text = (label || '?').slice(0, 2).toUpperCase();
  if (logoUrl && !failed) {
    return (
      <img
        src={logoUrl}
        alt=""
        className="h-8 w-8 rounded-full object-contain bg-ink-50 border border-ink-100"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div className="h-8 w-8 rounded-full bg-ink-100 flex items-center justify-center text-2xs font-semibold text-ink-700 shrink-0">
      {text}
    </div>
  );
};

const KpiCard = ({ label, value, sublabel, icon: Icon, tone }) => {
  const tones = {
    default: 'text-ink-900',
    success: 'text-success-700',
    danger: 'text-danger-700',
  };
  return (
    <div className="rounded-xl border border-ink-200 bg-white px-4 py-3.5">
      <div className="flex items-start justify-between gap-2">
        <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">{label}</p>
        {Icon && <Icon className="h-3.5 w-3.5 text-ink-300" />}
      </div>
      <p className={`mt-1.5 text-lg font-semibold tracking-tight tnum ${tones[tone || 'default']}`}>{value}</p>
      {sublabel && <p className="mt-0.5 text-xs text-ink-400">{sublabel}</p>}
    </div>
  );
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: Layers },
  { id: 'positions', label: 'Positions', icon: BarChart2 },
  { id: 'balances', label: 'Cash', icon: Wallet },
  { id: 'history', label: 'History', icon: TrendingUp },
];

const TabBar = ({ active, onChange, counts }) => (
  <div className="flex border-b border-ink-200 overflow-x-auto">
    {TABS.map((t) => {
      const Icon = t.icon;
      const isActive = active === t.id;
      return (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
            isActive
              ? 'border-accent-600 text-accent-700'
              : 'border-transparent text-ink-500 hover:text-ink-800 hover:border-ink-300'
          }`}
        >
          <Icon className="h-3.5 w-3.5" />
          {t.label}
          {counts?.[t.id] != null && (
            <span className={`text-xs rounded-full px-1.5 py-0.5 font-semibold ${isActive ? 'bg-accent-100 text-accent-700' : 'bg-ink-100 text-ink-500'}`}>
              {counts[t.id]}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

const PERIOD_OPTIONS = [
  { value: '1d', label: '1D' },
  { value: '1w', label: '1W' },
  { value: '1m', label: '1M' },
  { value: '3m', label: '3M' },
  { value: '1y', label: '1Y' },
];

const PeriodSelector = ({ value, onChange }) => (
  <div className="inline-flex rounded-md border border-ink-200 bg-ink-50 p-0.5">
    {PERIOD_OPTIONS.map((p) => (
      <button
        key={p.value}
        type="button"
        onClick={() => onChange(p.value)}
        className={`px-2.5 py-1 rounded-[5px] text-xs font-medium transition-colors ${
          value === p.value ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-700'
        }`}
      >
        {p.label}
      </button>
    ))}
  </div>
);

const SummaryRow = ({ label, value, indent, emphasize, tone }) => {
  const toneClass = tone === 'success' ? 'text-success-700' : tone === 'danger' ? 'text-danger-700' : emphasize ? 'font-semibold text-ink-900' : 'text-ink-700';
  return (
    <div className={`flex items-center justify-between py-2 gap-4 ${indent ? 'pl-4' : ''}`}>
      <span className={`text-sm ${indent ? 'text-ink-400' : 'text-ink-600'}`}>{indent ? `└ ${label}` : label}</span>
      <span className={`font-mono tnum text-sm text-right ${toneClass}`}>{value}</span>
    </div>
  );
};

const AssetAllocation = ({ rows, currency, invested }) => {
  if (!rows.length) return null;
  const chartData = rows.slice(0, 6).map((row, i) => ({
    name: row.symbol,
    value: row.marketValue,
    pct: invested > 0 ? (row.marketValue / invested) * 100 : 0,
    color: ALLOC_COLORS[i % ALLOC_COLORS.length],
  }));
  return (
    <div className="rounded-xl border border-ink-200 bg-white p-5 h-full">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-ink-900">Asset allocation</h3>
        <p className="text-xs text-ink-400 mt-0.5">
          {rows.length} position{rows.length === 1 ? '' : 's'} by market value
          {currency ? ` (${currency})` : ''}
        </p>
      </div>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" width={56} axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 11, fontWeight: 600 }} />
            <Tooltip
              cursor={{ fill: 'rgba(15,23,42,0.04)' }}
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const d = payload[0].payload;
                return (
                  <div className="rounded-md border border-ink-200 bg-white px-2.5 py-1.5 shadow-popover text-xs">
                    <p className="font-medium text-ink-900">{d.name}</p>
                    <p className="font-mono tnum text-ink-600">{fmtMoney(d.value, currency)}</p>
                    <p className="text-ink-400">{fmtPct(d.pct) || '—'}</p>
                  </div>
                );
              }}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={14}>
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const CurrencyAllocation = ({ wallets }) => {
  if (!wallets.length) return null;
  const total = wallets.reduce((s, w) => s + amountOrZero(w.cashBalance), 0);
  const singleCurrency = wallets.every((w) => w.currency === wallets[0].currency);
  const chartData = wallets.map((w, i) => ({
    name: w.currency,
    value: amountOrZero(w.cashBalance),
    pct: total > 0 ? (amountOrZero(w.cashBalance) / total) * 100 : 0,
    color: ALLOC_COLORS[i % ALLOC_COLORS.length],
  }));
  return (
    <div className="rounded-xl border border-ink-200 bg-white p-5 h-full">
      <div className="flex items-center justify-between mb-4 gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink-900">Cash by currency</h3>
          <p className="text-xs text-ink-400 mt-0.5">
            {wallets.length} balance{wallets.length === 1 ? '' : 's'}
            {singleCurrency ? '' : ' · native wallet amounts'}
          </p>
        </div>
        {singleCurrency && (
          <div className="text-right">
            <p className="text-2xs uppercase tracking-wider text-ink-400">Total</p>
            <p className="text-sm font-semibold tnum text-ink-900">{fmtMoney(total, wallets[0].currency)}</p>
          </div>
        )}
      </div>
      <div className="flex items-center gap-5">
        <div className="h-36 w-36 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={chartData} dataKey="value" innerRadius={42} outerRadius={62} paddingAngle={2} stroke="none">
                {chartData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.[0]) return null;
                  const d = payload[0].payload;
                  return (
                    <div className="rounded-md border border-ink-200 bg-white px-2.5 py-1.5 shadow-popover text-xs">
                      <p className="font-medium text-ink-900">{d.name}</p>
                      <p className="font-mono tnum text-ink-600">{fmtMoney(d.value, d.name)}</p>
                    </div>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="flex-1 space-y-2 min-w-0">
          {chartData.map((w) => (
            <li key={w.name} className="flex items-center justify-between text-sm gap-3">
              <span className="flex items-center gap-2 min-w-0">
                <span className="h-2 w-2 rounded-full shrink-0" style={{ background: w.color }} />
                <span className="font-medium text-ink-800">{w.name}</span>
              </span>
              <span className="font-mono tnum text-ink-600 text-right">
                {fmtNum(w.value)}
                <span className="block text-2xs text-ink-400">{fmtPct(w.pct)}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const OverviewTab = ({ portfolio, customerId }) => {
  const [period, setPeriod] = useState('1m');
  const [snapshots, setSnapshots] = useState(null);
  const [loading, setLoading] = useState(false);
  const currency = portfolio.currency;
  const rows = useMemo(() => portfolio.assets.map(assetRow), [portfolio.assets]);
  const wallets = useMemo(() => portfolio.cashBalances.map(cashRow), [portfolio.cashBalances]);
  const investedFromTotals = portfolio.totalInvestedMarketValue ?? 0;
  const invested = investedFromTotals > 0
    ? investedFromTotals
    : rows.reduce((sum, row) => sum + row.marketValue, 0);
  const unrealized = portfolio.totalUnrealizedPl;
  const plTone = unrealized == null ? undefined : unrealized >= 0 ? 'success' : 'danger';

  useEffect(() => {
    if (!customerId) return undefined;
    let cancelled = false;
    setLoading(true);
    portfolioService
      .getHistory(customerId, period)
      .then((data) => {
        if (cancelled) return;
        setSnapshots(Array.isArray(data?.snapshots) ? data.snapshots : Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setSnapshots([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, period]);

  const chartPoints = (snapshots || []).map((s) => ({
    date: s.snapshotDate,
    value: parseFloat(s.totalPortfolioValue ?? '0'),
  }));

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-ink-200 bg-white p-5">
        <div className="flex items-baseline justify-between gap-3 mb-3">
          <h3 className="text-sm font-semibold text-ink-900">Financial summary</h3>
          {currency && <span className="text-2xs font-semibold uppercase tracking-wider text-ink-400">{currency}</span>}
        </div>
        <div className="divide-y divide-ink-100">
          <SummaryRow label="Total portfolio value" value={fmtMoney(portfolio.totalSum, currency)} emphasize />
          <SummaryRow label="Invested market value" value={fmtMoney(portfolio.totalInvestedMarketValue, currency)} />
          <SummaryRow label="Invested cost" value={fmtMoney(portfolio.totalInvestedCost, currency)} />
          <SummaryRow
            label="Unrealized P&L"
            value={`${fmtSignedMoney(portfolio.totalUnrealizedPl, currency)}${fmtPct(portfolio.totalUnrealizedPlPercent) ? ` (${fmtPct(portfolio.totalUnrealizedPlPercent)})` : ''}`}
            tone={plTone}
            emphasize
          />
          <SummaryRow label="Total cash" value={fmtMoney(portfolio.totalCash, currency)} />
          <SummaryRow label="Available for trading" value={fmtMoney(portfolio.totalAvailableForTrading, currency)} indent />
          <SummaryRow label="Available for withdrawal" value={fmtMoney(portfolio.totalAvailableForWithdrawal, currency)} indent />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Positions', value: portfolio.assets.length },
          { label: 'Cash balances', value: portfolio.cashBalances.length },
          { label: 'Currencies', value: new Set(wallets.map((w) => w.currency).filter((c) => c && c !== '—')).size },
          { label: 'Display currency', value: currency || '—' },
        ].map((tile) => (
          <div key={tile.label} className="rounded-xl border border-ink-200 bg-white px-4 py-3">
            <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">{tile.label}</p>
            <p className="mt-1 text-xl font-semibold tnum text-ink-900">{tile.value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <AssetAllocation rows={rows} currency={currency} invested={invested} />
        <CurrencyAllocation wallets={wallets} />
      </div>

      <div className="rounded-xl border border-ink-200 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-ink-900">Portfolio value</h3>
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>
        <div className="h-40">
          {loading ? (
            <div className="h-full flex items-center justify-center text-ink-400">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : chartPoints.length < 2 ? (
            <div className="h-full flex items-center justify-center text-sm text-ink-400">No history for this period</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartPoints} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="portfolioValueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#4f46e5" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" hide />
                <YAxis hide domain={['dataMin', 'dataMax']} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.[0]) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="rounded-md border border-ink-200 bg-white px-2.5 py-1.5 shadow-popover">
                        <p className="text-xs font-mono tnum text-ink-900">{fmtMoney(d.value, currency)}</p>
                        <p className="text-2xs text-ink-400">{d.date}</p>
                      </div>
                    );
                  }}
                />
                <Area type="monotone" dataKey="value" stroke="#4f46e5" strokeWidth={1.5} fill="url(#portfolioValueGradient)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

const PositionsTab = ({ portfolio }) => {
  const currency = portfolio.currency;
  const rows = portfolio.assets.map(assetRow);
  const investedFromTotals = portfolio.totalInvestedMarketValue ?? 0;
  const invested = investedFromTotals > 0
    ? investedFromTotals
    : rows.reduce((sum, row) => sum + row.marketValue, 0);

  if (rows.length === 0) {
    return <EmptyState icon={BarChart2} title="No positions" description="This customer has no open positions." />;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 flex-wrap text-xs text-ink-500">
        <span><span className="font-semibold text-ink-800">{rows.length}</span> position{rows.length === 1 ? '' : 's'}</span>
        <span>
          Invested market value{' '}
          <span className="font-mono font-semibold text-ink-800 tnum">{fmtMoney(invested, currency)}</span>
        </span>
        <span>
          Unrealized P&amp;L{' '}
          <span className={`font-mono font-semibold tnum ${amountOrZero(portfolio.totalUnrealizedPl) >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
            {fmtSignedMoney(portfolio.totalUnrealizedPl, currency)}
          </span>
        </span>
      </div>
      <Table>
        <THead>
          <TR>
            <TH>Asset</TH>
            <TH className="w-20">Market</TH>
            <TH className="text-right w-24">Qty</TH>
            <TH className="text-right w-24">Available</TH>
            <TH className="text-right w-24">Reserved</TH>
            <TH className="text-right w-28">Avg cost</TH>
            <TH className="text-right w-28">Last</TH>
            <TH className="text-right w-32">Cost</TH>
            <TH className="text-right w-36">Market value</TH>
            <TH className="text-right w-20">Weight</TH>
            <TH className="text-right w-36">Unrealized P&amp;L</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map((row) => {
            const weight = invested > 0 ? (row.marketValue / invested) * 100 : 0;
            return (
              <TR key={row.key}>
                <TD>
                  <div className="flex items-center gap-3 min-w-[180px]">
                    <Mark logoUrl={row.logoUrl} label={row.symbol} />
                    <div className="min-w-0">
                      <p className="font-semibold text-ink-900">{row.symbol}</p>
                      {row.name && <p className="text-xs text-ink-500 truncate max-w-[220px]">{row.name}</p>}
                    </div>
                  </div>
                </TD>
                <TD>
                  <span className="text-xs font-medium text-ink-600">{row.marketSymbol || '—'}</span>
                </TD>
                <TD className="text-right">
                  <span className="font-mono text-sm tnum">{fmtQty(row.quantity)}</span>
                </TD>
                <TD className="text-right">
                  <span className="font-mono text-sm tnum text-ink-700">{fmtQty(row.availableQuantity)}</span>
                </TD>
                <TD className="text-right">
                  <span className="font-mono text-sm tnum text-ink-700">{fmtQty(row.reservedQuantity)}</span>
                </TD>
                <TD className="text-right">
                  <span className="font-mono text-sm tnum">{fmtNum(row.averageCost)}</span>
                  <NativeLine show={row.showNative} currency={row.listingCurrency} value={row.averageCostNative} />
                </TD>
                <TD className="text-right">
                  <span className="font-mono text-sm tnum text-ink-900">{fmtNum(row.lastPrice)}</span>
                  <NativeLine show={row.showNative} currency={row.listingCurrency} value={row.lastPriceNative} />
                </TD>
                <TD className="text-right">
                  <span className="font-mono text-sm tnum">{fmtNum(row.totalCost)}</span>
                  <NativeLine show={row.showNative} currency={row.listingCurrency} value={row.totalCostNative} />
                </TD>
                <TD className="text-right">
                  <span className="font-mono text-sm tnum font-medium text-ink-900">{fmtNum(row.marketValue)}</span>
                  <NativeLine show={row.showNative} currency={row.listingCurrency} value={row.marketValueNative} />
                  <div className="mt-1.5 h-1 rounded-full bg-ink-100 overflow-hidden">
                    <div className="h-full bg-accent-500 rounded-full" style={{ width: `${Math.min(weight, 100)}%` }} />
                  </div>
                </TD>
                <TD className="text-right">
                  <span className="font-mono text-xs tnum text-ink-500">{fmtPct(weight)?.replace('+', '') || '—'}</span>
                </TD>
                <TD className="text-right">
                  <PnL
                    value={row.unrealizedPl}
                    pct={row.unrealizedPlPercent}
                    native={row.showNative ? row.unrealizedPlNative : null}
                    nativeCurrency={row.showNative ? row.listingCurrency : null}
                  />
                </TD>
              </TR>
            );
          })}
        </TBody>
      </Table>
      <p className="text-xs text-ink-400">
        Amounts are in the display currency{currency ? ` (${currency})` : ''}. Listing-currency prices appear underneath when they differ.
      </p>
    </div>
  );
};

const BalancesTab = ({ portfolio }) => {
  const wallets = portfolio.cashBalances.map(cashRow);
  if (wallets.length === 0) {
    return <EmptyState icon={Wallet} title="No cash balances" description="This customer has no cash balances yet." />;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 flex-wrap text-xs text-ink-500">
        <span>
          Total cash{' '}
          <span className="font-mono font-semibold text-ink-800 tnum">{fmtMoney(portfolio.totalCash, portfolio.currency)}</span>
        </span>
        <span>
          For trading{' '}
          <span className="font-mono font-semibold text-ink-800 tnum">{fmtMoney(portfolio.totalAvailableForTrading, portfolio.currency)}</span>
        </span>
        <span>
          For withdrawal{' '}
          <span className="font-mono font-semibold text-ink-800 tnum">{fmtMoney(portfolio.totalAvailableForWithdrawal, portfolio.currency)}</span>
        </span>
      </div>
      <Table>
        <THead>
          <TR>
            <TH>Currency</TH>
            <TH className="text-right">Balance</TH>
            <TH className="text-right">For trading</TH>
            <TH className="text-right">For withdrawal</TH>
            <TH className="text-right">Reserved</TH>
            <TH className="text-right">Pending settlement</TH>
            <TH className="text-right">Pending consumed</TH>
          </TR>
        </THead>
        <TBody>
          {wallets.map((wallet) => (
            <TR key={wallet.key}>
              <TD>
                <div className="flex items-center gap-2.5">
                  <Mark logoUrl={wallet.logoUrl} label={wallet.currency} />
                  <div>
                    <p className="font-semibold text-ink-900 flex items-center gap-1.5">
                      <Landmark className="h-3.5 w-3.5 text-ink-400" />
                      {wallet.currency}
                    </p>
                    {wallet.precision != null && (
                      <p className="text-2xs text-ink-400">Precision {wallet.precision}</p>
                    )}
                  </div>
                </div>
              </TD>
              <TD className="text-right">
                <span className="font-mono text-sm tnum font-medium text-ink-900">{fmtNum(wallet.cashBalance)}</span>
              </TD>
              <TD className="text-right">
                <span className="font-mono text-sm tnum text-success-700">{fmtNum(wallet.availableForTrading)}</span>
              </TD>
              <TD className="text-right">
                <span className="font-mono text-sm tnum text-success-700">{fmtNum(wallet.availableForWithdrawal)}</span>
              </TD>
              <TD className="text-right">
                <span className="font-mono text-sm tnum text-ink-700">{fmtNum(wallet.totalReserved)}</span>
              </TD>
              <TD className="text-right">
                <span className="font-mono text-sm tnum text-warning-700">{fmtNum(wallet.pendingSettlements)}</span>
              </TD>
              <TD className="text-right">
                <span className="font-mono text-sm tnum text-ink-500">{fmtNum(wallet.pendingSettlementsConsumed)}</span>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      <p className="text-xs text-ink-400">
        Wallet amounts are in each balance currency. Portfolio cash totals above are in {portfolio.currency || 'the display currency'}.
      </p>
    </div>
  );
};

const HistoryTab = ({ customerId, currency }) => {
  const [period, setPeriod] = useState('1w');
  const [snapshots, setSnapshots] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!customerId) return undefined;
    setLoading(true);
    portfolioService
      .getHistory(customerId, period)
      .then((data) => setSnapshots(Array.isArray(data?.snapshots) ? data.snapshots : Array.isArray(data) ? data : []))
      .catch(() => setSnapshots([]))
      .finally(() => setLoading(false));
    return undefined;
  }, [customerId, period]);

  const chartPoints = (snapshots || []).map((s) => ({
    date: s.snapshotDate,
    value: parseFloat(s.totalPortfolioValue ?? '0'),
  }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Daily snapshots</p>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-ink-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading history…</span>
        </div>
      ) : !snapshots || snapshots.length === 0 ? (
        <EmptyState icon={TrendingUp} title="No history data" description="No portfolio snapshots found for this period." />
      ) : (
        <>
          {chartPoints.length >= 2 && (
            <div className="rounded-xl border border-ink-200 bg-white p-4 h-40">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartPoints} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="historyTabGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#4f46e5" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" hide />
                  <YAxis hide domain={['dataMin', 'dataMax']} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.[0]) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="rounded-md border border-ink-200 bg-white px-2.5 py-1.5 shadow-popover">
                          <p className="text-xs font-mono tnum text-ink-900">{fmtMoney(d.value, currency)}</p>
                          <p className="text-2xs text-ink-400">{d.date}</p>
                        </div>
                      );
                    }}
                  />
                  <Area type="monotone" dataKey="value" stroke="#4f46e5" strokeWidth={1.5} fill="url(#historyTabGradient)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
          <Table>
            <THead>
              <TR>
                <TH>Date</TH>
                <TH className="text-right w-40">Portfolio value</TH>
                <TH className="text-right w-36">Market value</TH>
                <TH className="text-right w-36">Cost basis</TH>
                <TH className="text-right w-28">Cash</TH>
                <TH className="text-right w-44">P&amp;L</TH>
              </TR>
            </THead>
            <TBody>
              {[...snapshots].reverse().map((s) => (
                <TR key={s.snapshotDate}>
                  <TD><span className="font-mono text-sm text-ink-700">{s.snapshotDate}</span></TD>
                  <TD className="text-right"><span className="font-mono text-sm tnum text-ink-900 font-semibold">{fmtNum(parseFloat(s.totalPortfolioValue ?? '0'))}</span></TD>
                  <TD className="text-right"><span className="font-mono text-sm tnum text-ink-700">{fmtNum(parseFloat(s.totalMarketValue ?? '0'))}</span></TD>
                  <TD className="text-right"><span className="font-mono text-sm tnum text-ink-600">{fmtNum(parseFloat(s.totalCostBasis ?? '0'))}</span></TD>
                  <TD className="text-right"><span className="font-mono text-sm tnum text-ink-700">{fmtNum(parseFloat(s.totalCash ?? '0'))}</span></TD>
                  <TD className="text-right"><PnL value={parseFloat(s.totalPnl ?? '0')} pct={s.totalPnlPercentage} /></TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </>
      )}
    </div>
  );
};

const PortfolioDetailPage = () => {
  const { customerId } = useParams();
  const [searchParams] = useSearchParams();
  const requestedCurrency = searchParams.get('displayCurrency') || '';
  const [customer, setCustomer] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayCurrency, setDisplayCurrency] = useState(
    PORTFOLIO_DISPLAY_CURRENCIES.includes(requestedCurrency) ? requestedCurrency : '',
  );
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    customersService.getCustomer(customerId).then(setCustomer).catch(() => null);
  }, [customerId]);

  const loadPortfolio = useCallback(() => {
    setLoading(true);
    setError(null);
    const options = displayCurrency ? { displayCurrency } : {};
    portfolioService
      .getCustomerPortfolio(customerId, options)
      .then((data) => setPortfolio(normalizeCustomerPortfolio(data)))
      .catch((err) => {
        setError(err?.response?.data?.message || 'Failed to load portfolio.');
        setPortfolio(null);
      })
      .finally(() => setLoading(false));
  }, [customerId, displayCurrency]);

  useEffect(() => { loadPortfolio(); }, [loadPortfolio]);

  const currency = portfolio?.currency || displayCurrency || '';
  const unrealized = portfolio?.totalUnrealizedPl;
  const plPos = (unrealized ?? 0) >= 0;

  const customerName = customer
    ? `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim() || customer.email
    : customerId;
  const initials = customer
    ? `${(customer.firstName || customer.email || '?')[0]}${(customer.lastName || '')[0] || ''}`.toUpperCase()
    : '??';

  const tabCounts = portfolio
    ? { positions: portfolio.assets.length, balances: portfolio.cashBalances.length }
    : {};

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Link to="/portfolio" className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-800">
          <ArrowLeft className="h-3.5 w-3.5" />
          All portfolios
        </Link>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-ink-500">
            Display currency
            <Select
              aria-label="Display currency"
              value={displayCurrency}
              onChange={(e) => setDisplayCurrency(e.target.value)}
              className="h-8 text-sm w-40"
            >
              <option value="">Account currency</option>
              {PORTFOLIO_DISPLAY_CURRENCIES.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </Select>
          </label>
          <button type="button" onClick={loadPortfolio} className="btn btn-sm btn-secondary">
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-ink-200 bg-white p-5 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-12 w-12 rounded-full bg-accent-50 text-accent-700 flex items-center justify-center text-sm font-semibold shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-lg font-semibold text-ink-900 truncate">{customerName || 'Portfolio'}</p>
              <p className="text-sm text-ink-500 truncate">{customer?.email || customerId}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">Total portfolio value</p>
            <p className="text-2xl font-semibold tracking-tight tnum text-ink-900">
              {loading ? '…' : fmtMoney(portfolio?.totalSum, currency)}
            </p>
            {!loading && portfolio && (
              <p className={`text-sm font-medium tnum mt-0.5 ${plPos ? 'text-success-700' : 'text-danger-700'}`}>
                {fmtSignedMoney(unrealized, currency)} unrealized
                {fmtPct(portfolio.totalUnrealizedPlPercent) ? ` (${fmtPct(portfolio.totalUnrealizedPlPercent)})` : ''}
              </p>
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-ink-200 bg-white px-4 py-3.5">
                <div className="h-3 w-16 bg-ink-100 animate-pulse rounded" />
                <div className="mt-3 h-6 w-24 bg-ink-100 animate-pulse rounded" />
              </div>
            ))}
          </div>
          <div className="h-64 rounded-xl border border-ink-200 bg-white animate-pulse" />
        </div>
      )}

      {!loading && error && (
        <Alert tone="danger" action={<button type="button" onClick={loadPortfolio} className="btn btn-sm btn-secondary">Retry</button>}>
          {error}
        </Alert>
      )}

      {!loading && !error && portfolio && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
            <KpiCard label="Invested value" value={fmtMoney(portfolio.totalInvestedMarketValue, currency)} sublabel="Market value" icon={BarChart2} />
            <KpiCard label="Invested cost" value={fmtMoney(portfolio.totalInvestedCost, currency)} sublabel="Cost basis" />
            <KpiCard label="Cash balance" value={fmtMoney(portfolio.totalCash, currency)} sublabel="All wallets" icon={Wallet} />
            <KpiCard label="For trading" value={fmtMoney(portfolio.totalAvailableForTrading, currency)} sublabel="Buying power" />
            <KpiCard label="Withdrawable" value={fmtMoney(portfolio.totalAvailableForWithdrawal, currency)} sublabel="Available to withdraw" />
            <KpiCard
              label="Unrealized"
              value={fmtSignedMoney(portfolio.totalUnrealizedPl, currency)}
              sublabel={fmtPct(portfolio.totalUnrealizedPlPercent) || 'Open P&L'}
              tone={plPos ? 'success' : 'danger'}
            />
          </div>

          <div className="rounded-xl border border-ink-200 bg-white overflow-hidden">
            <TabBar active={activeTab} onChange={setActiveTab} counts={tabCounts} />
            <div className="p-5">
              {activeTab === 'overview' && (
                <OverviewTab portfolio={portfolio} customerId={customerId} />
              )}
              {activeTab === 'positions' && <PositionsTab portfolio={portfolio} />}
              {activeTab === 'balances' && <BalancesTab portfolio={portfolio} />}
              {activeTab === 'history' && <HistoryTab customerId={customerId} currency={currency} />}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PortfolioDetailPage;
